/**
 * Hook bridging StrategyStateService to React components.
 *
 * Follows the same snapshot + event subscription pattern as useSizingState.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { useVisualization } from '@/context';
import type {
    FieldSelections,
    IAssemblyEntry,
    ICatalog,
    IntroductionSide,
    Side,
} from '@/workflows/vascular/common/catalog';
import { StrategyStateService } from '@/workflows/vascular/evar/services/strategy-state-service';

// ============================================================================
// Types
// ============================================================================

export interface StrategyStateSnapshot {
    selectedCatalogId: string | null;
    introductionSide: IntroductionSide;
    assembly: readonly IAssemblyEntry[];
    // Actions
    selectCatalog: (id: string | null) => void;
    setIntroductionSide: (side: IntroductionSide) => void;
    addPartFromCatalog: (
        catalog: ICatalog,
        partId: string,
        side: Side,
        fieldSelections: FieldSelections
    ) => string;
    removePart: (instanceId: string) => void;
    setFieldValue: (instanceId: string, column: string, value: string | number | null) => void;
    setResolvedRef: (instanceId: string, ref: string | null) => void;
    clearAssembly: (catalogId?: string) => void;
    clearStale: (instanceId: string) => void;
}

// ============================================================================
// Hook
// ============================================================================

export function useStrategyState(): StrategyStateSnapshot | null {
    const { getServiceManager } = useVisualization();
    const [snapshot, setSnapshot] = useState<StrategyStateSnapshot | null>(null);
    const serviceRef = useRef<StrategyStateService | null>(null);

    const actions = useMemo(
        () => ({
            selectCatalog: (id: string | null) => serviceRef.current?.selectCatalog(id),
            setIntroductionSide: (side: IntroductionSide) => serviceRef.current?.setIntroductionSide(side),
            addPartFromCatalog: (
                catalog: ICatalog,
                partId: string,
                side: Side,
                fieldSelections: FieldSelections
            ) => serviceRef.current?.addPartFromCatalog(catalog, partId, side, fieldSelections) ?? '',
            removePart: (instanceId: string) => serviceRef.current?.removePart(instanceId),
            setFieldValue: (instanceId: string, column: string, value: string | number | null) =>
                serviceRef.current?.setFieldValue(instanceId, column, value),
            setResolvedRef: (instanceId: string, ref: string | null) =>
                serviceRef.current?.setResolvedRef(instanceId, ref),
            clearAssembly: (catalogId?: string) => serviceRef.current?.clearAssembly(catalogId),
            clearStale: (instanceId: string) => serviceRef.current?.clearStale(instanceId),
        }),
        []
    );

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        if (!service || !service.isInitialized()) {
            setSnapshot(null);
            return;
        }

        setSnapshot({
            selectedCatalogId: service.getSelectedCatalogId(),
            introductionSide: service.getIntroductionSide(),
            assembly: service.getAssembly(),
            ...actions,
        });
    }, [actions]);

    useEffect(() => {
        const sm = getServiceManager();
        const service = sm.getService<StrategyStateService>(StrategyStateService.SERVICE_NAME);
        serviceRef.current = service;

        // If already initialized, build immediately
        if (service.isInitialized()) {
            buildSnapshot();
        }

        const unsubscribers = service.ALL_EVENTS.map((event) => service.subscribe(event, buildSnapshot));

        return () => {
            serviceRef.current = null;
            unsubscribers.forEach((unsub) => unsub());
        };
    }, [getServiceManager, buildSnapshot]);

    return snapshot;
}
